<template>
  <div class="event-filters">
    <div class="search-box">
      <div>
        <label
          for="event-filter-search"
          class="sr-only"
          >{{ $t('events.initialSearchAriaLabel') }}</label
        >
        <SearchTag
          v-if="searchTagText"
          :text="searchTagText"
          :on-remove="onRemoveTag"
        />
        <TextInput
          v-else
          id="event-filter-search"
          v-model="searchVal"
          type="search"
          :placeholder="$t('events.searchPlaceholder')"
          width="100%"
          @update:model-value="searchDebounce"
        />
      </div>
      <div>
        <img
          src="../../../assets/search-icon.svg"
          class="search-icon"
          alt=""
        />
      </div>
    </div>
    <a
      v-if="currentView === 'EventsListView' || currentView === 'EventsCalendarView'"
      href="#"
      class="skip-to-calendar"
      @click.prevent="emit('move-focus')"
    >
      {{ $t('events.skipToCalendar') }}
    </a>
    <div v-if="sections && sections.length > 1">
      <h3 class="title">
        {{ $t('events.calendars') }}
        <label class="sr-only">{{ $t('events.submit') }}</label>
        <button
          class="arrow"
          :class="{ 'upside-down': !showCalendars }"
          :aria-expanded="String(showCalendars)"
          aria-controls="event-calendars-block"
          @click="toggleCalendars"
        >
          <DropdownArrowIcon :aria-label="$t('events.collapseCalendarSectionAriaLabel')" />
        </button>
      </h3>
      <div
        v-show="showCalendars"
        id="event-calendars-block"
        class="block"
      >
        <FormCheckbox
          v-for="{ id, name } of sections"
          :key="`${id}-${name}`"
          :checkbox-id="`${id}-${name}`"
          class="filter-checkbox"
          :model-value="isChecked(EVENT_TYPE.CALENDAR, id)"
          @changed="checkboxChange(EVENT_TYPE.CALENDAR, $event, id, name)"
        >
          {{ name }}
        </FormCheckbox>
      </div>
    </div>

    <div
      v-for="(eventFilters, name, filterIdx) in formattedFilters"
      :key="name"
    >
      <h3 class="title">
        {{ name }}
        <button
          class="arrow"
          :class="{ 'upside-down': !selectedEventFilters.includes(name) }"
          :aria-expanded="String(selectedEventFilters.includes(name))"
          :aria-controls="`event-filters-block-${filterIdx}`"
          @click="toggleFilters(name)"
        >
          <DropdownArrowIcon :aria-label="getCollapseAriaLabel(name)" />
        </button>
        <label class="sr-only">{{ $t('events.submit') }}</label>
      </h3>
      <div
        v-show="selectedEventFilters.includes(name)"
        :id="`event-filters-block-${filterIdx}`"
        class="block"
      >
        <span class="filters-applied">
          {{ selectedFiltersText(eventFilters) }}
        </span>
        <FormCheckbox
          v-for="{ id, name: tagName } of eventFilters"
          :key="`${id}-${tagName}`"
          :checkbox-id="`${id}-${tagName}`"
          class="filter-checkbox"
          :model-value="isChecked(EVENT_TYPE.FILTER, id)"
          @changed="checkboxChange(EVENT_TYPE.FILTER, $event, id, tagName)"
        >
          {{ tagName }}
        </FormCheckbox>
      </div>
    </div>
  </div>
</template>

<script setup>
import debounce from 'lodash/debounce';
import { computed, onMounted, ref, watch } from 'vue';
import { useNuxtApp } from '#imports';
import { getSpecificListOrCalView } from '../components/helpers/events.helper';
import FormCheckbox from '../../global/Forms/FormCheckbox.vue';
import DropdownArrowIcon from './icons/DropdownArrowIcon.vue';
import TextInput from '../../global/Forms/TextInput';
import SearchTag from './EventViews/shared/SearchTag.vue';
import { useEventsStore } from '../../../stores/eventsStore.js';
import { useEventsAnnouncer } from '../../../composables/useEventsAnnouncer.js';

defineOptions({ name: 'EventsFiltersSelectors' });

const emit = defineEmits(['move-focus']);

const EVENT_TYPE = Object.freeze({
  CALENDAR: 'selectedCalendar',
  FILTER: 'selectedFilter',
});

const eventsStore = useEventsStore();
const { $t, $intl } = useNuxtApp();
const { announce } = useEventsAnnouncer();

const allEvents = computed(() => eventsStore.allEvents);
const totalUpcomingEvents = computed(() => eventsStore.totalUpcomingEvents);
const totalPastEvents = computed(() => eventsStore.totalPastEvents);
const selectedCalendars = computed(() => eventsStore.selectedCalendars);
const selectedFilters = computed(() => eventsStore.selectedFilters);
const filters = computed(() => eventsStore.filters);
const currentView = computed(() => eventsStore.currentView);
const queryParams = computed(() => eventsStore.queryParams);
const searchTagText = computed(() => eventsStore.searchTagText);
const searchText = computed(() => eventsStore.searchText);
const sections = computed(() => eventsStore.sections);

const formattedFilters = computed(() => {
  const formatted = {};
  filters.value.forEach((e) => {
    if (formatted[e.custom_section_name]) {
      formatted[e.custom_section_name].push(e);
    } else {
      formatted[e.custom_section_name] = [e];
    }
  });
  return formatted;
});

const searchVal = ref('');
const selectedEventFilters = ref([]);
const showCalendars = ref(true);

watch(allEvents, () => {
  if (!searchVal.value) return;
  announce(
    $t('events.searchResultsAriaLabel', {
      upcomingEvents: totalUpcomingEvents.value,
      pastEvents: totalPastEvents.value,
    }),
  );
});

onMounted(() => {
  searchVal.value = searchText.value;
  selectedEventFilters.value.push(...Object.keys(formattedFilters.value));
});

function onRemoveTag() {
  const today = new Date();
  eventsStore.setEventSearchTagText('');
  eventsStore.updateDateFilters({
    day: today.getDate(),
    month: today.getMonth(),
    year: today.getFullYear(),
  });
  return navigateTo({
    query: {
      id: undefined,
    },
  });
}

function checkboxChange(type, { checked }, value, tagName) {
  if (type === EVENT_TYPE.CALENDAR) {
    eventsStore.updateCalendars({
      checked,
      value,
      target: `${value}-${tagName}`,
    });
  } else {
    eventsStore.updateFilters({
      checked,
      value,
      target: `${value}-${tagName}`,
    });
  }

  if (searchTagText.value) {
    const today = new Date();
    eventsStore.setEventSearchTagText('');
    eventsStore.updateDateFilters({
      day: today.getDate(),
      month: today.getMonth(),
      year: today.getFullYear(),
    });
  }

  let params = {};
  if (searchText.value) {
    params = {
      section_ids: queryParams.value.section_ids ? queryParams.value.section_ids : undefined,
      filter_ids: queryParams.value.filter_ids ? queryParams.value.filter_ids : undefined,
      search: searchText.value,
    };
  } else {
    params = {
      section_ids: queryParams.value.section_ids ? queryParams.value.section_ids : undefined,
      filter_ids: queryParams.value.filter_ids ? queryParams.value.filter_ids : undefined,
      view: getSpecificListOrCalView(eventsStore),
    };
  }
  eventsStore.setQueryParams(params);
  return navigateTo({
    query: {
      ...params,
    },
  });
}

function isChecked(type, value) {
  if (type === EVENT_TYPE.CALENDAR) {
    return !!selectedCalendars.value.find((id) => parseInt(id, 10) === parseInt(value, 10));
  }
  return !!selectedFilters.value.find((id) => parseInt(id, 10) === parseInt(value, 10));
}

function toggleCalendars() {
  showCalendars.value = !showCalendars.value;
}

function toggleFilters(name) {
  if (selectedEventFilters.value.includes(name)) {
    selectedEventFilters.value = selectedEventFilters.value.filter((filter) => filter !== name);
  } else {
    selectedEventFilters.value.push(name);
  }
}

function selectedFiltersText(section) {
  let amount = 0;
  section.forEach((e) => {
    if (selectedFilters.value.includes(e.id)) {
      amount += 1;
    }
  });
  return $t('events.amountOfTagsApplied', {
    n: amount,
    amount: $intl.formatNumber(amount),
  });
}

function getCollapseAriaLabel(calendarName) {
  return $t('events.collapseCalendarNameSectionAriaLabel', {
    calendarName,
  });
}

async function searchCall() {
  await eventsStore.updateSearchText(searchVal.value);

  let nextQueryParams = {};
  if (searchVal.value) {
    nextQueryParams = {
      section_ids: queryParams.value.section_ids ? queryParams.value.section_ids : undefined,
      filter_ids: queryParams.value.filter_ids ? queryParams.value.filter_ids : undefined,
      search: searchVal.value,
    };
  } else {
    nextQueryParams = {
      section_ids: queryParams.value.section_ids ? queryParams.value.section_ids : undefined,
      filter_ids: queryParams.value.filter_ids ? queryParams.value.filter_ids : undefined,
      view: getSpecificListOrCalView(eventsStore),
      search: undefined,
    };
  }
  navigateTo({
    query: nextQueryParams,
  });
  setTimeout(() => {
    const searchBox = document.getElementById('event-filter-search');
    searchBox?.focus();
  }, 1000);
}

const searchDebounce = debounce(() => {
  void searchCall();
}, 500);
</script>

<style lang="scss" scoped>
@use '../../global/scss/mixins/breakpoints';
@use '../../global/scss/mixins/flex_mixin';
@use '../../global/scss/color_vars';

.event-filters {
  min-width: 220px;
  .search-box {
    margin-bottom: 32px;
  }

  .title {
    @include flex_mixin.flex-row;
    justify-content: space-between;
    align-items: center;

    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 20px;

    letter-spacing: 0.8px;
    text-transform: uppercase;

    color: #333333;
  }

  .block {
    border-left: 1px solid #00376c;
    padding: 0 16px;
    margin: 16px 0;
    @include flex_mixin.flex-row;
    flex-wrap: wrap;

    & .filter-checkbox {
      flex-basis: 100%;
      padding: 0 3px;
    }
  }

  .search-icon {
    position: relative;
    top: -20px;
    float: right;
    right: 10px;
  }

  .bob:focus-within {
    --tab-font-color-active: #{color_vars.$color-primary-dark};
    outline: var(--tab-font-color-active) auto 1px;
    box-shadow: 1px white;
    border-radius: 3px;
    padding: 1px;
  }

  .search-icon:focus {
    --tab-font-color-active: #{color_vars.$color-primary-dark};
    outline: var(--tab-font-color-active) auto 1px;
    box-shadow: 1px white;
    border-radius: 3px;
    padding: 1px;
  }

  .filters-applied {
    font-size: 14px;
    color: #747474;
    margin-bottom: 6px;
  }

  .upside-down {
    transform: rotate(180deg);
  }

  .arrow {
    transition: transform 150ms ease;
    background: none;
    border: none;
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: -webkit-focus-ring-color auto 5px;
    }
  }
}

:deep(.checkmark) {
  min-width: 16px !important;
}

.sr-only {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
}

.focus {
  outline: none !important;
}

.skip-to-calendar {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  &:focus {
    width: auto;
    height: auto;
    background-color: #f1f1f1;
    border-radius: 3px;
    clip: auto;
    clip-path: none;
    color: #21759b;
    font-size: 14px;
    font-weight: bold;
    left: 5px;
    padding: 15px 23px 14px;
    top: 5px;
    text-align: center;
    z-index: 20;
  }
}
</style>
